
import Vue from 'vue'
import { Project } from '../../types/main'
import * as t from '@/store/chisai/GCB2/taskList/actionTypes'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'

import api from '@root/src/api/api'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import Breadcrumbs from '@root/src/components/Breadcrumbs.vue'
import SmallHint from '@root/src/components/SmallHint.vue'
import BaseInputOutlined from '@root/src/components/inputs/BaseInputOutlined.vue'
import MagnifyIcon from '@root/src/assets/vuetifyIcons/magnify.vue'
import CustomActivatorSelect from '@root/src/components/inputs/CustomActivatorSelect.vue'
import DragOutlineIcon from '@root/src/assets/vuetifyIcons/dragOutline.vue'
import ArrowRightXsIcon from '@root/src/assets/vuetifyIcons/arrowRightXs.vue'
import ConfirmTechTagModal from '@root/src/components/ConfirmTechTagModal.vue'
import cfg from '@root/config'
import { getServiceTags, updateServiceTags } from '@root/src/api/tagging'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import Loading from '@root/src/components/project/Loading.vue'
import CustomSwitch from '@root/src/components/ui/CustomSwitch.vue'
import CheckIcon from '@root/src/assets/vuetifyIcons/check.vue'
import { getPseudoNetworkProjects } from '@root/src/api/projects'
const UNDEFINED_GROUP = 'Не обработано'
const SPECIAL_CLUSTER = 'Основные/дополнительные услуги'
export default Vue.extend({
    components: {
        SpinnerLoader,
        Breadcrumbs,
        SmallHint,
        BaseInputOutlined,
        MagnifyIcon,
        CustomActivatorSelect,
        DragOutlineIcon,
        ArrowRightXsIcon,
        ConfirmTechTagModal,
        Loading,
        CustomSwitch,
        CheckIcon
    },
    name: 'TaggingServices',
    props: {},
    data: () => ({
        loading: false,
        headerHintText: 'Данный виджет отвечает за присвоение услугам определенных тегов. Теги могут использоваться как группы для определенных активаций или отчетов или уведомлений. Обычно сотрудники DH производят настройку тегов при необходимости.',
        tagsData: [],
        servicesData: [],
        expanded: {} as Record<string, boolean>,
        searchQuery: '',
        debouncedSearchQuery: '',
        currentGroupService: null,
        currentFilter: { clusterName: null, groupName: null, subGroupName: null } as any,
        ServiceCategories: [],
        selectedCategory: [],
        currentClaster: null,
        selectedServiceIds: [] as string[],
        techConfirmVisible: false,
        techConfirmName: '',
        techConfirmRow: null as any,
        isDragging: false,
        draggingIds: [] as string[],
        dragPreviewEl: null as HTMLElement | null,
        dragOverHandler: null as ((e: DragEvent) => void) | null,
        autoScrollInterval: null as number | null,
        currentHoveredRow: null as HTMLElement | null,
        saveQueue: null as Promise<void> | null | 'end',
        archiveFilter: null as null | boolean,
        allArchive: false as boolean,
        filial: null as string | null,
        filialList: [] as any[],
        initialLoaded: false,
        visibleServiceIds: [] as string[],
        serviceRowHeightsById: {} as Record<string, number>,
        averageServiceRowHeight: 96,
        averageServiceRowHeightInitialized: false,
        lastServicesScrollTop: 0,
        currentServicesScrollDirection: 'idle' as 'idle' | 'down' | 'up',
        lastServicesScrollAt: 0,
        lastServicesScrollSpeed: 0,
        visibleServiceRangeStart: -1,
        visibleServiceRangeEnd: -1,
    }),
    computed: {
        isHrProject(): boolean {
            return this.project.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false
        },
        groupFilterLabel(): string {
            const sel = this.selectedCategory
            if (!Array.isArray(sel) || sel.length === 0) return 'Все'
            if (sel.length === 1) {
                const v: any = sel[0]
                return v || 'Нет категории'
            }
            return `Группы (${sel.length})`
        },
        filteredServices(): any[] {
            const byTag = (svc: any) => {
                const f = this.currentFilter || {}
                const clusterName = f.clusterName
                const groupName = f.groupName
                const subGroupName = f.subGroupName
                const hasFilter = !!clusterName || !!groupName || !!subGroupName
                if (!hasFilter) return true
                const techStatus = this.mapTechGroupNameToStatus(groupName)
                return svc.tags.some((t: any) => {
                    if (subGroupName) return t.clusterName === clusterName && t.groupName === groupName && t.subGroupName === subGroupName
                    if (groupName) {
                        if (techStatus) return t.clusterName === clusterName && t.status === techStatus
                        return t.clusterName === clusterName && t.groupName === groupName
                    }
                    return t.clusterName === clusterName
                })
            }
            const bySearch = (svc: any) => {
                const q = (this.debouncedSearchQuery || '').trim().toLowerCase()
                if (!q) return true
                return (svc.name || '').toLowerCase().includes(q)
            }
            const byCategory = (svc: any) => {
                const selected = this.selectedCategory
                if (!Array.isArray(selected) || selected.length === 0) return true
                const svcName = (svc.crmGroupName || '').trim().toLowerCase()
                return selected.some((it: any) => {
                    if (typeof it === 'string') return it.trim().toLowerCase() === svcName
                    const val = (it && (it.name || it.id)) ? String(it.name || it.id) : ''
                    return val.trim().toLowerCase() === svcName
                })
            }
            const byArchive = (svc: any) => {
                if (this.archiveFilter === null) return true
                return this.archiveFilter === Boolean(svc.archive)
            }
            return (this.servicesData || []).filter((svc: any) => byTag(svc) && byCategory(svc) && bySearch(svc) && byArchive(svc))
        },
        serviceIndexById(): Record<string, number> {
            return this.filteredServices.reduce((acc: Record<string, number>, svc: any, index: number) => {
                acc[String(svc.id)] = index
                return acc
            }, {} as Record<string, number>)
        },
        servicesCountsIndex(): Record<string, number> {
            return this.buildServiceCountsIndex((this.servicesData || []) as any[])
        },
        selectedServicesCountsIndex(): Record<string, number> {
            const selected = new Set((this.selectedServiceIds || []).map((id: any) => String(id)))
            const selectedServices = (this.servicesData || []).filter((svc: any) => selected.has(String(svc.id)))
            return this.buildServiceCountsIndex(selectedServices as any[])
        },
        needToConfirmCountsIndex(): Record<string, number> {
            const counts: Record<string, number> = {}
            const services = (this.servicesData || []) as any[]
            services.forEach((svc: any) => {
                const tags = Array.isArray(svc?.tags) ? svc.tags : []
                if (!tags.length) return
                const uniqueKeysPerService = new Set<string>()
                tags.forEach((t: any) => {
                    if (String(t?.status || '') !== 'needToConfirm') return
                    const clusterName = String(t?.clusterName || '')
                    const groupName = String(t?.groupName || '')
                    const subGroupName = String(t?.subGroupName || '')
                    if (clusterName && groupName) {
                        uniqueKeysPerService.add(this.buildGroupCountKey(clusterName, groupName))
                    }
                    if (clusterName && groupName && subGroupName) {
                        uniqueKeysPerService.add(this.buildSubGroupCountKey(clusterName, groupName, subGroupName))
                    }
                })
                uniqueKeysPerService.forEach((key) => {
                    counts[key] = (counts[key] || 0) + 1
                })
            })
            return counts
        },
        crmGroupCategories(): any[] {
            const set = new Set<string>()
            for (const svc of (this.servicesData || []) as any[]) {
                const g = (svc.crmGroupName || '').trim()
                if (g) set.add(g)
            }
            const items = [{name: 'Нет категории', id: null}]
            items.push(...Array.from(set).sort().map(name => ({ id: name, name })))
            return items
        },
        hasSelectedServices(): boolean {
            return (this.selectedServiceIds || []).length > 0
        },
        allFilteredSelected(): boolean {
            const filtered = this.filteredServices
            if (!filtered.length) return false
            const selected = new Set(this.selectedServiceIds)
            return filtered.every((s: any) => selected.has(s.id))
        },
        someFilteredSelected(): boolean {
            const filtered = this.filteredServices
            if (!filtered.length) return false
            const selected = new Set(this.selectedServiceIds)
            const any = filtered.some((s: any) => selected.has(s.id))
            const all = filtered.every((s: any) => selected.has(s.id))
            return any && !all
        },
        headerSelectKey(): string {
            return `${this.allFilteredSelected ? 'all' : 'none'}-${this.someFilteredSelected ? 'some' : 'nosome'}-${(this.filteredServices || []).length}-${(this.selectedServiceIds || []).length}`
        },
        flatTags(): any[] {
            const result: any[] = []
            const walk = (nodes: any[], level: number, path: string[]) => {
                if (!Array.isArray(nodes)) return
                for (const n of nodes) {
                    const hasChildren = Array.isArray(n.children) && n.children.length > 0
                    const uid = [...path, n.id].join('>')
                    result.push({
                        id: n.id,
                        name: n.name,
                        count: n.count,
                        color: n.color || '#2A3B51',
                        level,
                        hasChildren,
                        uid,
                    })
                    if (hasChildren && !!this.expanded[uid]) {
                        walk(n.children, level + 1, [...path, n.id])
                    }
                }
            }
            walk(this.tagsData, 0, [])
            return result
        },
        breadcrumbs(): any {
            return [
                {
                    text: this.project.name,
                    to: `/project/${this.project.id}`,
                },
                {
                    text: 'Тегирование услуг',
                    to: '',
                    disabled: true,
                },
            ]
        },
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        isSuperUser(): boolean {
            return cfg.adminsWhitelist.includes(this.$auth.user.sub)
        },
        isTeymurov(): boolean {
            return cfg.teymurovId === this.$auth.user.sub
        }
    },
    watch: {
        'project.timestamps.communicationUpdate': {
            immediate: true,
            async handler(val) {
                if (!this.initialLoaded && this.isHrProject) {
                    const res = await getPseudoNetworkProjects({projectId: this.project.id})
                    if(res.error) {
                        this.$store.dispatch('callNotify', 'Ошибка при загрузке данных')      
                    } else {
                        this.filialList = (res.data || []).filter(el => !el.isMain)
                        this.filial = this.filialList[0]?.id
                    }
                }
                await this.initData()
                if (this.saveQueue === 'end') {
                    this.saveQueue = null
                }
                if (!this.initialLoaded && this.isHrProject) {
                    this.initialLoaded = true
                }
            },
        },
        filial: {
            async handler(val) {
                if (this.initialLoaded && this.isHrProject) {
                    await this.initData()
                }
            },
        },
        searchQuery: {
            immediate: true,
            handler(val) {
                const next = String(val || '')
                if (!next.trim()) {
                    ;(this.applyDebouncedSearch as any).cancel?.()
                    this.debouncedSearchQuery = ''
                    return
                }
                this.applyDebouncedSearch(next)
            },
        },
        filteredServices: {
            immediate: true,
            handler() {
                this.recalculateServicesVirtualization()
            },
            deep: false,
        },
    },
    methods: {
        applyDebouncedSearch: debounce(function (this: any, query: string) {
            this.debouncedSearchQuery = String(query || '')
        }, 400),
        buildClusterCountKey(clusterName: string): string {
            return `cluster:${String(clusterName || '')}`
        },
        buildGroupCountKey(clusterName: string, groupName: string): string {
            return `group:${String(clusterName || '')}>${String(groupName || '')}`
        },
        buildSubGroupCountKey(clusterName: string, groupName: string, subGroupName: string): string {
            return `subgroup:${String(clusterName || '')}>${String(groupName || '')}>${String(subGroupName || '')}`
        },
        buildStatusCountKey(clusterName: string, status: string): string {
            return `status:${String(clusterName || '')}>${String(status || '')}`
        },
        buildServiceCountsIndex(services: any[]): Record<string, number> {
            const counts: Record<string, number> = {}
            const safeServices = Array.isArray(services) ? services : []
            safeServices.forEach((svc: any) => {
                const tags = Array.isArray(svc?.tags) ? svc.tags : []
                if (!tags.length) return
                const uniqueKeysPerService = new Set<string>()
                tags.forEach((t: any) => {
                    const clusterName = String(t?.clusterName || '')
                    if (!clusterName) return
                    uniqueKeysPerService.add(this.buildClusterCountKey(clusterName))
                    const groupName = String(t?.groupName || '')
                    if (groupName) uniqueKeysPerService.add(this.buildGroupCountKey(clusterName, groupName))
                    const subGroupName = String(t?.subGroupName || '')
                    if (groupName && subGroupName) uniqueKeysPerService.add(this.buildSubGroupCountKey(clusterName, groupName, subGroupName))
                    const status = String(t?.status || '')
                    if (status) uniqueKeysPerService.add(this.buildStatusCountKey(clusterName, status))
                })
                uniqueKeysPerService.forEach((key) => {
                    counts[key] = (counts[key] || 0) + 1
                })
            })
            return counts
        },
        resolveRowTagNames(row: any): { clusterName: string; groupName: string; subGroupName: string } {
            if (!row) return { clusterName: '', groupName: '', subGroupName: '' }
            if (row.level === 0) return { clusterName: String(row.name || ''), groupName: '', subGroupName: '' }
            if (row.level === 1) {
                const parts = String(row.uid || '').split('>')
                const clusterId = parts[0]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                const clusterName = String(clusterNode ? clusterNode.name : clusterId || '')
                return { clusterName, groupName: String(row.name || ''), subGroupName: '' }
            }
            const parts = String(row.uid || '').split('>')
            const clusterId = parts[0]
            const groupId = parts[1]
            const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
            const groupNode = clusterNode?.children?.find((c: any) => c.id === groupId)
            const clusterName = String(clusterNode ? clusterNode.name : clusterId || '')
            const groupName = String(groupNode ? groupNode.name : groupId || '')
            return { clusterName, groupName, subGroupName: String(row.name || '') }
        },
        getServiceRowOuterHeight(row: HTMLElement): number {
            if (!row) return 0
            const rectHeight = row.getBoundingClientRect().height || 0
            const styles = window.getComputedStyle(row)
            const marginTop = parseFloat(styles.marginTop || '0') || 0
            const marginBottom = parseFloat(styles.marginBottom || '0') || 0
            return rectHeight + marginTop + marginBottom
        },
        refreshVisibleServiceRowHeights() {
            if (!this.$el) return
            const rows = Array.from(this.$el.querySelectorAll('.service-row[data-service-id]')) as HTMLElement[]
            if (!rows.length) return
            const measured: number[] = []
            rows.forEach((row) => {
                const id = String(row.dataset.serviceId || '')
                if (!id) return
                const height = this.getServiceRowOuterHeight(row)
                if (height > 0) {
                    this.$set(this.serviceRowHeightsById, id, height)
                    measured.push(height)
                }
            })
            if (measured.length) {
                const measuredAvg = measured.reduce((sum, h) => sum + h, 0) / measured.length
                if (!this.averageServiceRowHeightInitialized) {
                    this.averageServiceRowHeight = Math.round(measuredAvg * 100) / 100
                    this.averageServiceRowHeightInitialized = true
                    return
                }
                // При "флинге" не дергаем среднюю высоту, чтобы избежать скачков скролла.
                if (this.lastServicesScrollSpeed > 0.9) return
                const currentAvg = this.averageServiceRowHeight || measuredAvg
                const relativeDiff = Math.abs(measuredAvg - currentAvg) / Math.max(currentAvg, 1)
                // Игнорируем микрошум высоты.
                if (relativeDiff < 0.05) return
                const target = currentAvg * 0.85 + measuredAvg * 0.15
                const maxStep = Math.max(2, currentAvg * 0.06)
                const nextAvg = Math.max(currentAvg - maxStep, Math.min(currentAvg + maxStep, target))
                this.averageServiceRowHeight = Math.round(nextAvg * 100) / 100
            }
        },
        isServiceVisible(serviceId: string): boolean {
            return this.visibleServiceIds.includes(String(serviceId))
        },
        getServicePlaceholderHeight(serviceId: string): number {
            return Math.round(this.serviceRowHeightsById[String(serviceId)] || this.averageServiceRowHeight)
        },
        updateVisibleServicesByScroll(scrollTop: number, viewportHeight: number, overscan: number) {
            if (!this.filteredServices.length) return
            const avg = this.averageServiceRowHeight || 1
            const beforeBufferPx = Math.max(viewportHeight * 0.5, overscan * avg)
            const afterBufferPx = Math.max(viewportHeight * 0.8, overscan * avg)
            const targetTop = Math.max(0, scrollTop - beforeBufferPx)
            const targetBottom = scrollTop + viewportHeight + afterBufferPx

            let accumulatedHeight = 0
            let start = 0
            for (let i = 0; i < this.filteredServices.length; i += 1) {
                const svc = this.filteredServices[i]
                const rowHeight = this.serviceRowHeightsById[String(svc?.id)] || avg
                if (accumulatedHeight + rowHeight >= targetTop) {
                    start = i
                    break
                }
                accumulatedHeight += rowHeight
            }

            let end = start
            let visibleHeight = accumulatedHeight
            for (let i = start; i < this.filteredServices.length; i += 1) {
                const svc = this.filteredServices[i]
                const rowHeight = this.serviceRowHeightsById[String(svc?.id)] || avg
                visibleHeight += rowHeight
                end = i
                if (visibleHeight >= targetBottom) break
            }

            start = Math.max(0, start - 2)
            end = Math.min(this.filteredServices.length - 1, end + 2)
            const hasRange = this.visibleServiceRangeStart >= 0 && this.visibleServiceRangeEnd >= 0
            const isMinorRangeShift =
                hasRange &&
                Math.abs(start - this.visibleServiceRangeStart) < 2 &&
                Math.abs(end - this.visibleServiceRangeEnd) < 2
            if (isMinorRangeShift) return
            const nextVisibleIds: string[] = []
            for (let i = start; i <= end; i += 1) {
                const svc = this.filteredServices[i]
                if (svc?.id != null) nextVisibleIds.push(String(svc.id))
            }
            if (!nextVisibleIds.length && this.filteredServices.length) {
                nextVisibleIds.push(String(this.filteredServices[0].id))
            }
            const currentSet = new Set(this.visibleServiceIds)
            const nextSet = new Set(nextVisibleIds)
            const hasChanges =
                nextVisibleIds.length !== this.visibleServiceIds.length ||
                nextVisibleIds.some((id) => !currentSet.has(id)) ||
                this.visibleServiceIds.some((id) => !nextSet.has(id))
            if (!hasChanges) return
            this.visibleServiceIds = nextVisibleIds
            this.visibleServiceRangeStart = start
            this.visibleServiceRangeEnd = end
            this.$nextTick(() => this.refreshVisibleServiceRowHeights())
        },
        syncVisibleServicesByViewport: throttle(function (this: any) {
            const body = this.$refs.servicesBody as HTMLElement | undefined
            if (!body || !this.filteredServices.length) return
            const now = Date.now()
            const currentScrollTop = body.scrollTop || 0
            const delta = currentScrollTop - this.lastServicesScrollTop
            const dt = now - (this.lastServicesScrollAt || now) || 1
            this.currentServicesScrollDirection = delta > 1 ? 'down' : delta < -1 ? 'up' : 'idle'
            this.lastServicesScrollTop = currentScrollTop
            this.lastServicesScrollAt = now
            const speed = Math.abs(delta) / dt
            this.lastServicesScrollSpeed = speed
            let overscan = 8
            if (speed > 1.2) {
                overscan = 28
            } else if (speed > 0.5) {
                overscan = 18
            }
            this.updateVisibleServicesByScroll(currentScrollTop, body.clientHeight || 0, overscan)
        }, 40),
        recalculateServicesVirtualization() {
            const body = this.$refs.servicesBody as HTMLElement | undefined
            const currentScrollTop = body ? body.scrollTop || 0 : 0
            this.lastServicesScrollTop = currentScrollTop
            this.lastServicesScrollAt = Date.now()
            this.lastServicesScrollSpeed = 0
            this.visibleServiceRangeStart = -1
            this.visibleServiceRangeEnd = -1
            this.$nextTick(() => {
                this.refreshVisibleServiceRowHeights()
                const body = this.$refs.servicesBody as HTMLElement | undefined
                const viewportHeight = body ? body.clientHeight || 0 : this.averageServiceRowHeight * 10
                const nextScrollTop = body ? body.scrollTop || 0 : 0
                this.updateVisibleServicesByScroll(nextScrollTop, viewportHeight, 12)
            })
        },
        resetServicesVirtualization() {
            this.visibleServiceIds = []
            const body = this.$refs.servicesBody as HTMLElement | undefined
            if (body) body.scrollTop = 0
            this.recalculateServicesVirtualization()
        },
        slugifyId(name: string): string {
            const base = String(name || '').trim().toLowerCase()
            const cleaned = base
                .replace(/\s+/g, '-')
                .replace(/[^\p{L}\p{N}-]+/gu, '')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '')
            return cleaned || 'node'
        },
        normalizeTagsTree(nodes: any[]): any[] {
            if (!Array.isArray(nodes)) return []
            const walk = (arr: any[]): any[] => {
                const localUsed = new Set<string>()
                return (arr || []).map(raw => {
                    const node = { ...raw }
                    let id: string = node.id
                    if (!id || typeof id !== 'string') {
                        id = this.slugifyId(node.name)
                    } else {
                        id = String(id)
                    }
                    let uniq = id
                    let k = 1
                    while (localUsed.has(uniq)) { uniq = `${id}-${k++}` }
                    localUsed.add(uniq)
                    node.id = uniq
                    if (Array.isArray(node.children) && node.children.length) {
                        node.children = walk(node.children)
                    }
                    return node
                })
            }
            const normalized = walk(nodes)
            const globalSeen = new Set<string>()
            for (const n of normalized) {
                let id = n.id
                let i = 1
                while (globalSeen.has(id)) { id = `${n.id}-${i++}` }
                globalSeen.add(id)
                n.id = id
            }
            return normalized
        },
        onServiceDragStart(svc: any, e: DragEvent) {
            const selected = new Set(this.selectedServiceIds)
            const ids = this.hasSelectedServices ? Array.from(selected) : [svc.id]
            this.draggingIds = ids
            this.isDragging = true
            
            // Добавляем класс к body для отключения hover эффектов
            document.body.classList.add('dragging')
            // Это для крос браузерности
            try { e.dataTransfer?.setData('text/plain', JSON.stringify({ type: 'services', ids })) } catch (_) {}
            e.dataTransfer && (e.dataTransfer.effectAllowed = 'move')
            // Эта штука чтобы убрать прозрачность перетаскиваемой карточки
            const count = ids.length
            const title = count === 1 ? (svc && svc.name ? String(svc.name) : '1 услуга') : 
                (count % 10 === 1 && count % 100 !== 11) ? `${count} услуга` :
                (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) ? `${count} услуги` : `${count} услуг`
            const height = 44
            const leftBarWidth = 24
            const gap = 12
            const paddingRight = 12
            const fontSpec = '14px Geologica, sans-serif'
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')!
            ctx.font = fontSpec
            const textMetrics = ctx.measureText(title)
            const textWidth = Math.ceil(textMetrics.width)
            const width = leftBarWidth + gap + textWidth + paddingRight
            canvas.width = width * 2
            canvas.height = height * 2
            ctx.scale(2, 2)
            // задняя часть карточки
            const radius = 4
            ctx.fillStyle = '#EBF4FF'
            ctx.beginPath()
            ctx.moveTo(radius, 0)
            ctx.lineTo(width - radius, 0)
            ctx.quadraticCurveTo(width, 0, width, radius)
            ctx.lineTo(width, height - radius)
            ctx.quadraticCurveTo(width, height, width - radius, height)
            ctx.lineTo(radius, height)
            ctx.quadraticCurveTo(0, height, 0, height - radius)
            ctx.lineTo(0, radius)
            ctx.quadraticCurveTo(0, 0, radius, 0)
            ctx.closePath()
            ctx.fill()
            // тени
            ctx.save()
            ctx.shadowColor = 'rgba(42, 59, 81, 0.12)'
            ctx.shadowBlur = 12
            ctx.shadowOffsetY = 3
            ctx.restore()
            // левая часть
            ctx.fillStyle = '#D4D8DC'
            ctx.beginPath()
            ctx.moveTo(8, 0)
            ctx.lineTo(leftBarWidth - 0, 0)
            ctx.lineTo(leftBarWidth - 0, height)
            ctx.lineTo(8, height)
            ctx.closePath()
            ctx.fill()
            // текст
            ctx.fillStyle = '#2A3B51'
            ctx.font = fontSpec
            ctx.textBaseline = 'middle'
            ctx.fillText(title, leftBarWidth + gap, height / 2)
            if (e.dataTransfer && (e.dataTransfer as any).setDragImage) {
                // Некоторые браузеры скрывают/делают полупрозрачным dragImage — показываем свой оверлей, а нативный скрываем
                const img = new Image()
                img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADElEQVQIW2P4z8AAAAMBAQDjv0iUAAAAAElFTkSuQmCC'
                ;(e.dataTransfer as any).setDragImage(img, 0, 0)
            }

            // Параллельно рисуем фиксированный оверлей, который будет следовать за курсором
            const overlay = document.createElement('div')
            overlay.className = 'drag-overlay'
            overlay.innerHTML = `<div class=\"drag-overlay__left\" style=\"color:black\">\n  <svg width=\"24\" height=\"24\" viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n    <path d=\"M11.125 5.0625C11.125 6.22266 10.1797 7.125 9.0625 7.125C7.90234 7.125 7 6.22266 7 5.0625C7 3.94531 7.90234 3 9.0625 3C10.1797 3 11.125 3.94531 11.125 5.0625ZM9.0625 4.375C8.67578 4.375 8.375 4.71875 8.375 5.0625C8.375 5.44922 8.67578 5.75 9.0625 5.75C9.40625 5.75 9.75 5.44922 9.75 5.0625C9.75 4.71875 9.40625 4.375 9.0625 4.375ZM11.125 11.9375C11.125 13.0977 10.1797 14 9.0625 14C7.90234 14 7 13.0977 7 11.9375C7 10.8203 7.90234 9.875 9.0625 9.875C10.1797 9.875 11.125 10.8203 11.125 11.9375ZM9.0625 11.25C8.67578 11.25 8.375 11.5938 8.375 11.9375C8.375 12.3242 8.67578 12.625 9.0625 12.625C9.40625 12.625 9.75 12.3242 9.75 11.9375C9.75 11.5938 9.40625 11.25 9.0625 11.25ZM7 18.8125C7 17.6953 7.90234 16.75 9.0625 16.75C10.1797 16.75 11.125 17.6953 11.125 18.8125C11.125 19.9727 10.1797 20.875 9.0625 20.875C7.90234 20.875 7 19.9727 7 18.8125ZM9.0625 19.5C9.40625 19.5 9.75 19.1992 9.75 18.8125C9.75 18.4688 9.40625 18.125 9.0625 18.125C8.67578 18.125 8.375 18.4688 8.375 18.8125C8.375 19.1992 8.67578 19.5 9.0625 19.5ZM18 5.0625C18 6.22266 17.0547 7.125 15.9375 7.125C14.7773 7.125 13.875 6.22266 13.875 5.0625C13.875 3.94531 14.7773 3 15.9375 3C17.0547 3 18 3.94531 18 5.0625ZM15.9375 4.375C15.5508 4.375 15.25 4.71875 15.25 5.0625C15.25 5.44922 15.5508 5.75 15.9375 5.75C16.2812 5.75 16.625 5.44922 16.625 5.0625C16.625 4.71875 16.2812 4.375 15.9375 4.375ZM13.875 11.9375C13.875 10.8203 14.7773 9.875 15.9375 9.875C17.0547 9.875 18 10.8203 18 11.9375C18 13.0977 17.0547 14 15.9375 14C14.7773 14 13.875 13.0977 13.875 11.9375ZM15.9375 12.625C16.2812 12.625 16.625 12.3242 16.625 11.9375C16.625 11.5938 16.2812 11.25 15.9375 11.25C15.5508 11.25 15.25 11.5938 15.25 11.9375C15.25 12.3242 15.5508 12.625 15.9375 12.625ZM18 18.8125C18 19.9727 17.0547 20.875 15.9375 20.875C14.7773 20.875 13.875 19.9727 13.875 18.8125C13.875 17.6953 14.7773 16.75 15.9375 16.75C17.0547 16.75 18 17.6953 18 18.8125ZM15.9375 18.125C15.5508 18.125 15.25 18.4688 15.25 18.8125C15.25 19.1992 15.5508 19.5 15.9375 19.5C16.2812 19.5 16.625 19.1992 16.625 18.8125C16.625 18.4688 16.2812 18.125 15.9375 18.125Z\" fill=\"currentColor\"/>\n    <path d=\"M11.125 5.0625C11.125 6.22266 10.1797 7.125 9.0625 7.125C7.90234 7.125 7 6.22266 7 5.0625C7 3.94531 7.90234 3 9.0625 3C10.1797 3 11.125 3.94531 11.125 5.0625ZM9.0625 4.375C8.67578 4.375 8.375 4.71875 8.375 5.0625C8.375 5.44922 8.67578 5.75 9.0625 5.75C9.40625 5.75 9.75 5.44922 9.75 5.0625C9.75 4.71875 9.40625 4.375 9.0625 4.375ZM11.125 11.9375C11.125 13.0977 10.1797 14 9.0625 14C7.90234 14 7 13.0977 7 11.9375C7 10.8203 7.90234 9.875 9.0625 9.875C10.1797 9.875 11.125 10.8203 11.125 11.9375ZM9.0625 11.25C8.67578 11.25 8.375 11.5938 8.375 11.9375C8.375 12.3242 8.67578 12.625 9.0625 12.625C9.40625 12.625 9.75 12.3242 9.75 11.9375C9.75 11.5938 9.40625 11.25 9.0625 11.25ZM7 18.8125C7 17.6953 7.90234 16.75 9.0625 16.75C10.1797 16.75 11.125 17.6953 11.125 18.8125C11.125 19.9727 10.1797 20.875 9.0625 20.875C7.90234 20.875 7 19.9727 7 18.8125ZM9.0625 19.5C9.40625 19.5 9.75 19.1992 9.75 18.8125C9.75 18.4688 9.40625 18.125 9.0625 18.125C8.67578 18.125 8.375 18.4688 8.375 18.8125C8.375 19.1992 8.67578 19.5 9.0625 19.5ZM18 5.0625C18 6.22266 17.0547 7.125 15.9375 7.125C14.7773 7.125 13.875 6.22266 13.875 5.0625C13.875 3.94531 14.7773 3 15.9375 3C17.0547 3 18 3.94531 18 5.0625ZM15.9375 4.375C15.5508 4.375 15.25 4.71875 15.25 5.0625C15.25 5.44922 15.5508 5.75 15.9375 5.75C16.2812 5.75 16.625 5.44922 16.625 5.0625C16.625 4.71875 16.2812 4.375 15.9375 4.375ZM13.875 11.9375C13.875 10.8203 14.7773 9.875 15.9375 9.875C17.0547 9.875 18 10.8203 18 11.9375C18 13.0977 17.0547 14 15.9375 14C14.7773 14 13.875 13.0977 13.875 11.9375ZM15.9375 12.625C16.2812 12.625 16.625 12.3242 16.625 11.9375C16.625 11.5938 16.2812 11.25 15.9375 11.25C15.5508 11.25 15.25 11.5938 15.25 11.9375C15.25 12.3242 15.5508 12.625 15.9375 12.625ZM18 18.8125C18 19.9727 17.0547 20.875 15.9375 20.875C14.7773 20.875 13.875 19.9727 13.875 18.8125C13.875 17.6953 14.7773 16.75 15.9375 16.75C17.0547 16.75 18 17.6953 18 18.8125ZM15.9375 18.125C15.5508 18.125 15.25 18.4688 15.25 18.8125C15.25 19.1992 15.5508 19.5 15.9375 19.5C16.2812 19.5 16.625 19.1992 16.625 18.8125C16.625 18.4688 16.2812 18.125 15.9375 18.125Z\" fill=\"white\" fill-opacity=\"0.6\"/>\n  </svg>\n</div><div class=\"drag-overlay__right\" style=\"font-family: Geologica, sans-serif; color: #2A3B51\">${title}</div>`
            document.body.appendChild(overlay)
            this.dragPreviewEl = overlay
            const move = (evt: DragEvent) => {
                // располагам карточку прямо под курсором: лёгкий отрицательный сдвиг, чтобы курсор был на области перетаскивания
                const x = (evt.clientX || 0) - 12
                const y = (evt.clientY || 0) - 20
                overlay.style.transform = `translate(${x}px, ${y}px)`
                
                ;(this as any).lastMouseY = evt.clientY || 0
            }
            this.dragOverHandler = move
            document.addEventListener('dragover', move)
            
            this.startAutoScroll()
        },
        onServiceDragEnd() {
            this.isDragging = false
            this.draggingIds = []
            
            document.body.classList.remove('dragging')
            
            if (this.currentHoveredRow) {
                this.clearHoverEffect(this.currentHoveredRow)
                this.currentHoveredRow = null
            }
            
            const tagNodes = document.querySelectorAll('.tag-node')
            tagNodes.forEach((node: Element) => {
                (node as HTMLElement).style.backgroundColor = ''
            })
            
            if (this.dragOverHandler) {
                document.removeEventListener('dragover', this.dragOverHandler)
                this.dragOverHandler = null
            }
            this.stopAutoScroll()
            if (this.dragPreviewEl && this.dragPreviewEl.parentNode) {
                this.dragPreviewEl.parentNode.removeChild(this.dragPreviewEl)
            }
            this.dragPreviewEl = null
        },
        startAutoScroll() {
            if (this.autoScrollInterval) {
                clearInterval(this.autoScrollInterval)
            }
            
            this.autoScrollInterval = window.setInterval(() => {
                if (!this.isDragging) return
                
                const lastMouseY = (this as any).lastMouseY || 0
                const viewportHeight = window.innerHeight
                const scrollThreshold = 45 // Расстояние от края для начала скролла
                const scrollSpeed = 9 // Скорость скролла
                
                if (lastMouseY < scrollThreshold) {
                    window.scrollBy(0, -scrollSpeed)
                }
                else if (lastMouseY > viewportHeight - scrollThreshold) {
                    window.scrollBy(0, scrollSpeed)
                }
            }, 14)
        },
        stopAutoScroll() {
            if (this.autoScrollInterval) {
                clearInterval(this.autoScrollInterval)
                this.autoScrollInterval = null
            }
        },
        onTagDragOver(row: any, e: DragEvent) {
            if (this.canAddTagHere(row)) {
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
                
                const target = e.target as HTMLElement
                const tagNode = target.closest('.tag-node') as HTMLElement
                if (tagNode) {
                    if (this.currentHoveredRow && this.currentHoveredRow !== tagNode) {
                        this.clearHoverEffect(this.currentHoveredRow)
                    }
                    
                    tagNode.style.backgroundColor = '#f4f5f6'
                    this.currentHoveredRow = tagNode
                }
            } else {
                if (e.dataTransfer) e.dataTransfer.dropEffect = 'none'
                
                if (this.currentHoveredRow) {
                    this.clearHoverEffect(this.currentHoveredRow)
                    this.currentHoveredRow = null
                }
            }
            
            (this as any).lastMouseY = e.clientY || 0
        },
        onTagDrop(row: any, e: DragEvent) {
            if (!this.canAddTagHere(row)) return
            
            const ids = this.draggingIds.length ? this.draggingIds : []
            const wasInitiallySelected = this.selectedServiceIds.length > 0
            
            if (wasInitiallySelected) {
                const prevSelected = new Set(this.selectedServiceIds)
                const areSame = ids.length && ids.every(id => prevSelected.has(id)) && ids.length === prevSelected.size
                if (!areSame) {
                    this.selectedServiceIds = ids
                }
            } else {
                this.selectedServiceIds = ids
            }
            
            this.addTagToSelected(row)
            
            this.onServiceDragEnd()
        },

        onTagMouseEnter(row: any, e: MouseEvent) {
            if (this.isDragging) {
                const target = e.target as HTMLElement
                const tagRow = target.closest('.tag-row') as HTMLElement
                if (tagRow && tagRow.classList.contains('droppable')) {
                    tagRow.style.backgroundColor = '#f4f5f6'
                }
            }
        },

        onTagMouseLeave(row: any, e: MouseEvent) {
            if (this.isDragging) {
                const target = e.target as HTMLElement
                const tagRow = target.closest('.tag-row') as HTMLElement
                if (tagRow && tagRow.classList.contains('droppable')) {
                    tagRow.style.backgroundColor = row.level === 0 ? '#f8f9fa' : ''
                }
            }
        },

        clearHoverEffect(tagNode: HTMLElement) {
            tagNode.style.backgroundColor = ''
        },

        onContainerDragLeave(e: DragEvent) {
            const container = e.currentTarget as HTMLElement
            const rect = container.getBoundingClientRect()
            const x = e.clientX
            const y = e.clientY
            
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                if (this.currentHoveredRow) {
                    this.clearHoverEffect(this.currentHoveredRow)
                    this.currentHoveredRow = null
                }
            }
        },
        softRemoveTag(serviceId: string, tagIndex: number) {
            const svc = (this.servicesData as any[]).find((s: any) => s.id === serviceId)
            if (!svc) return
            const tags = [...(svc.tags || [])]
            const tag = tags[tagIndex]
            if (!tag) return
            tags[tagIndex] = { ...tag, __removing: true }
            this.updateServiceTags(serviceId, tags)
            setTimeout(() => {
                const fresh = (this.servicesData as any[]).find((s: any) => s.id === serviceId)
                if (!fresh) return
                const updated = [...(fresh.tags || [])]
                const idx = updated.findIndex((t: any) => t === tags[tagIndex] || (t.clusterName === tag.clusterName && t.groupName === tag.groupName && t.subGroupName === tag.subGroupName && t.status === tag.status))
                if (idx >= 0) {
                    updated.splice(idx, 1)
                    this.updateServiceTags(serviceId, updated)
                }
            }, 600)
        },
        getActionButtonsType(svc: any, idx: number | string): 'default' | 'remove-only' {
            const tags = svc.tags || [];
            const currentTag = tags[idx];
            if (!currentTag) return 'default';
            
            const clusterName = currentTag.clusterName;
            
            // Исключаем специальные кластеры
            if (clusterName === 'Основные/дополнительные услуги') {
                return 'default';
            }
            
            // Считаем твердые теги в этом кластере (кроме текущего)
            const solidTagsCount = tags.filter((t: any, i: number) => 
                i !== idx && 
                t.clusterName === clusterName && 
                (t.status === 'confirmed' || (t.status === 'needToConfirm' && !!t.groupName))
            ).length;
            
            // Если есть другие твердые теги - показываем только удаление
            return solidTagsCount > 0 ? 'remove-only' : 'default';
        },
        mapTechGroupNameToStatus(name?: string | null): string | null {
            if (!name) return null
            const map: Record<string, string> = {
                'Не обработано': 'unassigned',
                'Тег не требуется': 'delayed',
                'Требует подтверждения': 'needToConfirm',
            }
            return map[name] || null
        },
        isSelected(id: string): boolean {
            return (this.selectedServiceIds || []).includes(id)
        },
        toggleSelectService(id: string) {
            const set = new Set(this.selectedServiceIds)
            if (set.has(id)) set.delete(id)
            else set.add(id)
            this.selectedServiceIds = Array.from(set)
            const selectedSvc = this.servicesData.filter(svc => this.selectedServiceIds.includes(svc.id))
            const acrhived = selectedSvc.filter(svc => svc.archive)
            const notAcrhived = selectedSvc.filter(svc => !svc.archive)
            if ((acrhived.length && !notAcrhived.length) || (!this.selectedServiceIds.length && this.archiveFilter)) {
                this.allArchive = true
            } else {
                this.allArchive = false
            }
        },
        toggleSelectAll(val: boolean) {
            if (val) {
                this.selectedServiceIds = (this.filteredServices || []).map((s: any) => s.id)
            } else {
                const filteredIds = new Set((this.filteredServices || []).map((s: any) => s.id))
                this.selectedServiceIds = (this.selectedServiceIds || []).filter(id => !filteredIds.has(id))
            }
            const selectedSvc = this.servicesData.filter(svc => this.selectedServiceIds.includes(svc.id))
            const acrhived = selectedSvc.filter(svc => svc.archive)
            const notAcrhived = selectedSvc.filter(svc => !svc.archive)
            if ((acrhived.length && !notAcrhived.length) || (!this.selectedServiceIds.length && this.archiveFilter)) {
                this.allArchive = true
            } else {
                this.allArchive = false
            }
        },
        onHeaderCheckboxChange(val: boolean) {
            if (this.someFilteredSelected && !this.allFilteredSelected) {
                const filteredIds = new Set((this.filteredServices || []).map((s: any) => s.id))
                this.selectedServiceIds = (this.selectedServiceIds || []).filter(id => !filteredIds.has(id))
                return
            }
            this.toggleSelectAll(val)
        },
        onHeaderCheckboxClick(e: MouseEvent) {
            if (this.someFilteredSelected && !this.allFilteredSelected) {
                const filteredIds = new Set((this.filteredServices || []).map((s: any) => s.id))
                this.selectedServiceIds = (this.selectedServiceIds || []).filter(id => !filteredIds.has(id))
                return
            }
            const next = !this.allFilteredSelected
            this.toggleSelectAll(next)
        },
        canAddTagHere(row: any): boolean {
            const forbidden = ['Требует подтверждения']
            if (row.level === 0) return false
            if (row.level === 1) return !forbidden.includes(row.name)
            return true
        },
        addTagToSelected(row: any, force: boolean = false) {
            const { clusterName, groupName, subGroupName } = this.extractNamesFromRow(row)
            const selected = new Set(this.selectedServiceIds)
            if (!force && row.level === 1 && (groupName === 'Не обработано' || groupName === 'Тег не требуется') && selected.size >= 2) {
                const statusSolid = new Set(['confirmed','needToConfirm'])
                const hasSolid = (this.servicesData || []).some((svc: any) => selected.has(svc.id) && (svc.tags || []).some((t: any) => t.clusterName === clusterName && statusSolid.has(t.status)))
                if (hasSolid) {
                    this.techConfirmVisible = true
                    this.techConfirmName = groupName
                    this.techConfirmRow = row
                    return
                }
            }
            const updatedServices: any[] = []
            this.servicesData = (this.servicesData || []).map((svc: any) => {
                if (!selected.has(svc.id)) return svc
                if (row.level === 1 && (groupName === 'Не обработано' || groupName === 'Тег не требуется')) {
                    const status = groupName === 'Не обработано' ? 'unassigned' : 'delayed'
                    const tags = (svc.tags || []).filter((t: any) => t.clusterName !== clusterName)
                    const newTag = { clusterName, groupName, subGroupName: null, status }
                    const nextSvc = { ...svc, tags: [...tags, newTag] }
                    if (!isEqual(svc.tags, nextSvc.tags)) updatedServices.push(nextSvc)
                    return nextSvc
                }
                let tags = [...(svc.tags || [])]
                tags = tags.filter((t: any) => !(
                    t.clusterName === clusterName && (
                        t.status === 'unassigned' ||
                        t.status === 'delayed' ||
                        (t.status === 'needToConfirm' && (!t.groupName || (t.groupName === UNDEFINED_GROUP) || t.groupName === groupName))
                    )
                ))
                if (clusterName === SPECIAL_CLUSTER) {
                    if (subGroupName) {
                        (row as any).level = 1
                    }
                    tags = tags.filter((t: any) => !(t.clusterName === clusterName && t.groupName && t.groupName !== groupName))
                }
                if (subGroupName) {
                    tags = tags.filter((t: any) => !(
                        t.clusterName === clusterName &&
                        t.groupName === groupName &&
                        (t.subGroupName == null) &&
                        t.status === 'confirmed'
                    ))
                }
                const norm = (v: any) => (v == null ? '' : String(v))
                const keyOf = (t: any) => `${norm(t.clusterName)}>${norm(t.groupName)}>${norm(t.subGroupName)}`
                const newKey = `${norm(clusterName)}>${norm(groupName)}>${clusterName === SPECIAL_CLUSTER ? '' : norm(subGroupName)}`
                let replaced = false
                const nextTags: any[] = []
                for (const t of tags) {
                    if (keyOf(t) === newKey) {
                        if (!replaced) {
                            if (t.status === 'needToConfirm') {
                                nextTags.push({ ...t, status: 'confirmed' })
                            } else {
                                nextTags.push(t)
                            }
                            replaced = true
                        }
                    } else {
                        nextTags.push(t)
                    }
                }
                if (!replaced) {
                    nextTags.push({ clusterName, groupName, subGroupName: clusterName === SPECIAL_CLUSTER ? null : subGroupName, status: 'confirmed' })
                }
                const nextSvc = { ...svc, tags: nextTags }
                if (!isEqual(svc.tags, nextSvc.tags)) updatedServices.push(nextSvc)
                return nextSvc
            })
            if (updatedServices.length) this.saveServices(updatedServices)
            this.selectedServiceIds = []
        },
        confirmAddTech() {
            if (!this.techConfirmRow) { this.techConfirmVisible = false; return }
            const row = this.techConfirmRow
            this.techConfirmVisible = false
            this.techConfirmRow = null
            this.addTagToSelected(row, true)
        },
        cancelAddTech() {
            this.techConfirmVisible = false
            this.techConfirmRow = null
        },
        extractNamesFromRow(row: any) {
            if (row.level === 1) {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                return { clusterName: clusterNode ? clusterNode.name : row.name, groupName: row.name, subGroupName: null }
            }
            if (row.level === 2) {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const groupId = parts[1]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                const groupNode = clusterNode?.children?.find((c: any) => c.id === groupId)
                return { clusterName: clusterNode ? clusterNode.name : '', groupName: groupNode ? groupNode.name : row.name, subGroupName: row.name }
            }
            return { clusterName: null, groupName: null, subGroupName: null }
        },
        approveTag(svc: any, idx: number | string) {
            const tags = [...(svc.tags || [])]
            tags[idx] = { ...tags[idx], status: 'confirmed' }
            this.updateServiceTags(svc.id, tags)
        },
        markAsDelayed(svc: any, idx: number | string) {
            const originalTags = svc.tags || []
            const current = originalTags[idx]
            if (!current) return
            const clusterName = current.clusterName
            const hasOtherSolid = originalTags.some((t: any, i: number) => i !== idx && t.clusterName === clusterName && (t.status === 'confirmed' || (t.status === 'needToConfirm' && !!t.groupName)))
            if (hasOtherSolid) {
                const shouldPurge = (t: any) => t.clusterName === clusterName && (
                    t.status === 'unassigned' ||
                    t.status === 'delayed' ||
                    (t.status === 'needToConfirm' && (!t.groupName || (t.groupName === UNDEFINED_GROUP) || t.groupName === current.groupName))
                )
                const purged = originalTags.filter((t: any, i: number) => i !== idx && !shouldPurge(t))
                this.updateServiceTags(svc.id, purged)
                const newSvcBeforeRemoval = (this.servicesData as any[]).find((s: any) => s.id === svc.id)
                if (newSvcBeforeRemoval) {
                    const remainingInCluster = (newSvcBeforeRemoval.tags || []).filter((t: any, i: number) => t.clusterName === clusterName && !(t.clusterName === current.clusterName && t.groupName === current.groupName && t.subGroupName === current.subGroupName && t.status === current.status))
                    if (remainingInCluster.length === 0) {
                        const withFallback = [
                            ...(newSvcBeforeRemoval.tags || []),
                            { clusterName, groupName: 'Тег не требуется', subGroupName: null, status: 'delayed' },
                        ]
                        this.updateServiceTags(svc.id, withFallback)
                    }
                    const newIdx = ((this.servicesData as any[]).find((s: any) => s.id === svc.id)?.tags || []).findIndex((t: any) => t.clusterName === current.clusterName && t.groupName === current.groupName && t.subGroupName === current.subGroupName && t.status === current.status)
                    if (newIdx >= 0) this.softRemoveTag(svc.id, newIdx)
                }
                return
            }
            let kept = originalTags.map((t: any, i: number) => i === idx ? { ...t, status: 'delayed', groupName: 'Тег не требуется', subGroupName: null } : t)
            kept = kept.filter((t: any, i: number) => !(t.clusterName === clusterName && (i !== idx) && (
                t.status === 'unassigned' ||
                t.status === 'delayed' ||
                (t.status === 'needToConfirm' && ((!t.groupName && !t.subGroupName) || t.groupName === current.groupName))
            )))
            this.updateServiceTags(svc.id, kept)
        },
        markAsUnassigned(svc: any, idx: number | string) {
            const originalTags = svc.tags || []
            const current = originalTags[idx]
            if (!current) return
            const clusterName = current.clusterName
            const hasOtherSolid = originalTags.some((t: any, i: number) => i !== idx && t.clusterName === clusterName && (t.status === 'confirmed' || (t.status === 'needToConfirm' && !!t.groupName)))
            if (hasOtherSolid) {
                const shouldPurge = (t: any) => t.clusterName === clusterName && (
                    t.status === 'unassigned' ||
                    t.status === 'delayed' ||
                    (t.status === 'needToConfirm' && (!t.groupName || t.groupName === UNDEFINED_GROUP || t.groupName === current.groupName))
                )
                const purged = originalTags.filter((t: any, i: number) => i !== idx && !shouldPurge(t))
                this.updateServiceTags(svc.id, purged)
                const newSvcBeforeRemoval = (this.servicesData as any[]).find((s: any) => s.id === svc.id)
                if (newSvcBeforeRemoval) {
                    const remainingInCluster = (newSvcBeforeRemoval.tags || []).filter((t: any, i: number) => t.clusterName === clusterName && !(t.clusterName === current.clusterName && t.groupName === current.groupName && t.subGroupName === current.subGroupName && t.status === current.status))
                    if (remainingInCluster.length === 0) {
                        const withFallback = [
                            ...(newSvcBeforeRemoval.tags || []),
                            { clusterName, groupName: 'Не обработано', subGroupName: null, status: 'unassigned' },
                        ]
                        this.updateServiceTags(svc.id, withFallback)
                    }
                    const newIdx = ((this.servicesData as any[]).find((s: any) => s.id === svc.id)?.tags || []).findIndex((t: any) => t.clusterName === current.clusterName && t.groupName === current.groupName && t.subGroupName === current.subGroupName && t.status === current.status)
                    if (newIdx >= 0) this.softRemoveTag(svc.id, newIdx)
                }
                return
            }
            let kept = originalTags.map((t: any, i: number) => i === idx ? { ...t, status: 'unassigned', groupName: 'Не обработано', subGroupName: null } : t)
            kept = kept.filter((t: any, i: number) => !(t.clusterName === clusterName && (i !== idx) && (
                t.status === 'unassigned' ||
                t.status === 'delayed' ||
                (t.status === 'needToConfirm' && ((!t.groupName && !t.subGroupName) || t.groupName === current.groupName))
            )))
            this.updateServiceTags(svc.id, kept)
        },
        updateServiceTags(serviceId: string, tags: any[]) {
            let changed: any | null = null
            this.servicesData = (this.servicesData || []).map((s: any) => {
                if (s.id === serviceId) {
                    const next = { ...s, tags }
                    if (!isEqual(s.tags, next.tags)) changed = next
                    return next
                }
                return s
            })
            if (changed) this.saveServices([changed])
        },
        archiveToggle(services) {
            this.saveServices([services])
            const selectedSvc = this.servicesData.filter(svc => this.selectedServiceIds.includes(svc.id))
            const acrhived = selectedSvc.filter(svc => svc.archive)
            const notAcrhived = selectedSvc.filter(svc => !svc.archive)
            if (acrhived.length && !notAcrhived.length) {
                this.allArchive = true
            } else {
                this.allArchive = false
            }
        },
        isTagSelected(row: any): boolean {
            const f: any = this.currentFilter || {}
            const hasFilter = !!f.clusterName || !!f.groupName || !!f.subGroupName
            if (!hasFilter) return false
            if (row.level === 0) {
                return f.clusterName === row.name && !f.groupName && !f.subGroupName
            } else if (row.level === 1) {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                const clusterName = clusterNode ? clusterNode.name : clusterId
                return f.clusterName === clusterName && f.groupName === row.name && !f.subGroupName
            } else {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const groupId = parts[1]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                const groupNode = clusterNode?.children?.find((c: any) => c.id === groupId)
                const clusterName = clusterNode ? clusterNode.name : clusterId
                const groupName = groupNode ? groupNode.name : groupId
                return f.clusterName === clusterName && f.groupName === groupName && f.subGroupName === row.name
            }
        },
        statusClass(status: string): string {
            const map: Record<string, string> = {
                'unassigned': 'status-red',
                'delayed': 'status-orange',
                'needToConfirm': 'status-blue',
                'confirmed': '',
            }
            return map[status] || ''
        },
        async initData() {
            this.loading = true
            const { data, error } = await getServiceTags({projectId: this.project.id, isHr: this.isHrProject, filial: this.filial})
            if (error) {
                this.$store.dispatch('callNotify', 'Ошибка при загрузке данных')                
            }
            this.tagsData = data.filtersData || []
            this.servicesData = data.servicesData || []
            this.tagsData = this.normalizeTagsTree(this.tagsData)
            // при любой полной перезагрузке данных (в т.ч. смена филиала) сбрасываем выбор услуг
            this.selectedServiceIds = []
            this.allArchive = false
            this.loading = false
            this.resetServicesVirtualization()
        },
        isBold(row) {
            const boldIdList = ['needToConfirm', 'delayed', 'unassigned']
            const exp1 = row.hasChildren
            const exp2 = boldIdList.includes(row.id)
            return exp1 || exp2
        },
        toggle(uid: string) {
            this.$set(this.expanded, uid, !this.expanded[uid])
        },
        applyFilter(row: any) {
            const empty = { clusterName: null, groupName: null, subGroupName: null }
            let next: any = { ...empty }
            if (row.level === 0) {
                next.clusterName = row.name
            } else if (row.level === 1) {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                next.clusterName = clusterNode ? clusterNode.name : clusterId
                next.groupName = row.name
            } else {
                const parts = row.uid.split('>')
                const clusterId = parts[0]
                const groupId = parts[1]
                const clusterNode = (this.tagsData || []).find((n: any) => n.id === clusterId)
                const groupNode = clusterNode?.children?.find((c: any) => c.id === groupId)
                next.clusterName = clusterNode ? clusterNode.name : clusterId
                next.groupName = groupNode ? groupNode.name : groupId
                next.subGroupName = row.name
            }
            const prev = this.currentFilter || empty
            const isSame = prev.clusterName === next.clusterName && prev.groupName === next.groupName && prev.subGroupName === next.subGroupName
            this.currentFilter = isSame ? empty : next
        },
        getNodeCount(row: any): number {
            const index = this.servicesCountsIndex || {}
            const { clusterName, groupName, subGroupName } = this.resolveRowTagNames(row)
            if (!clusterName) return 0
            if (row.level === 0) {
                return index[this.buildClusterCountKey(clusterName)] || 0
            }
            if (row.level === 1) {
                const techStatus = this.mapTechGroupNameToStatus(groupName)
                if (techStatus) return index[this.buildStatusCountKey(clusterName, techStatus)] || 0
                return index[this.buildGroupCountKey(clusterName, groupName)] || 0
            }
            return index[this.buildSubGroupCountKey(clusterName, groupName, subGroupName)] || 0
        },
        getSelectedCount(row: any): number {
            const index = this.selectedServicesCountsIndex || {}
            const { clusterName, groupName, subGroupName } = this.resolveRowTagNames(row)
            if (!clusterName) return 0
            if (row.level === 0) {
                return index[this.buildClusterCountKey(clusterName)] || 0
            }
            if (row.level === 1) {
                const techStatus = this.mapTechGroupNameToStatus(groupName)
                if (techStatus) return index[this.buildStatusCountKey(clusterName, techStatus)] || 0
                return index[this.buildGroupCountKey(clusterName, groupName)] || 0
            }
            return index[this.buildSubGroupCountKey(clusterName, groupName, subGroupName)] || 0
        },
        search(e: Event) {},
        getColorActBtn(tag) {
            const status = tag.status
            if(status === 'unassigned') return '#EE3939'
            if(status === 'needToConfirm') return '#378EFF'
            if(status === 'delayed') return '#FD9C2B'
            return '#2A3B51'
        },
        hasNeedToConfirm(row: any): boolean {
            if (!row || row.level === 0) return false
            const techNames = new Set(['Не обработано', 'Тег не требуется', 'Требует подтверждения'])
            if (row.level === 1 && techNames.has(row.name)) return false
            const index = this.needToConfirmCountsIndex || {}
            if (row.level === 1) {
                const { clusterName, groupName } = this.resolveRowTagNames(row)
                if (!clusterName || !groupName) return false
                return (index[this.buildGroupCountKey(clusterName, groupName)] || 0) > 0
            }
            if (row.level === 2) {
                const { clusterName, groupName, subGroupName } = this.resolveRowTagNames(row)
                if (!clusterName || !groupName || !subGroupName) return false
                return (index[this.buildSubGroupCountKey(clusterName, groupName, subGroupName)] || 0) > 0
            }
            return false
        },
        async saveServices(services) {
            const arr = Array.isArray(services) ? services : [services]
            const run = async () => {
                await updateServiceTags({ projectId: this.project.id, isHr: this.isHrProject, filial: this.filial , services: arr.map(s => ({ id: s.id, tags: s.tags, archive: s.archive || false })) })
            }
            const chain = (this.saveQueue && this.saveQueue !== 'end' ? this.saveQueue : Promise.resolve()).then(() => run()).catch(err => {
                console.error('saveServices failed', err)
            }) as Promise<void>
            const current = chain.then(() => {
                if (this.saveQueue === current) {
                    this.saveQueue = 'end'
                }
            }) as Promise<void>
            this.saveQueue = current
            return current
        },
        resetFilter(type) {
            if (type === 'cluster') {
                this.currentFilter = {
                    clusterName: null,
                    groupName: null,
                    subGroupName: null
                }
            } else if (type === 'search') {
                this.searchQuery = ''
            } else if (type === 'crmGroupName') {
                this.selectedCategory = []
            } else return
        },
        setArchiveFilter(status) {
            this.archiveFilter = status
            if (this.selectedServiceIds.length) return
            if (status) {
                this.allArchive = true
            } else {
                this.allArchive = false
            }
        },
        async setArchiveToggleSelected() {
            const selectedIds = this.selectedServiceIds
            const selectedScv = this.servicesData.filter(svc => selectedIds.includes(svc.id) && this.allArchive !== Boolean(svc.archive))
            const svcToSave = selectedScv.map(svc => {
                return {
                    ...svc,
                    archive: this.allArchive
                }
            })
            if(svcToSave.length) {
                svcToSave.forEach(el => {
                    const svcToUpdate = this.servicesData.find(svc => svc.id === el.id)
                    svcToUpdate.archive = this.allArchive
                })
                this.saveServices(svcToSave)
            }
            if(typeof this.archiveFilter === 'boolean' && this.selectedServiceIds.length) {
                this.selectedServiceIds = []
            }                
        }
    },
    mounted() {
        if (!this.isSuperUser && !this.isTeymurov && this.project.id !== 'TXZONX0H5') {
            this.$router.push(`/project/${this.project.id}`)
            return
        }
    },
    beforeDestroy() {
        // Если компонент уничтожается во время перетаскивания — обязательно снимаем глобальные слушатели/таймеры.
        if (this.dragOverHandler) {
            document.removeEventListener('dragover', this.dragOverHandler)
            this.dragOverHandler = null
        }
        try {
            this.stopAutoScroll()
        } catch (e) {}
        document.body.classList.remove('dragging')
        if (this.dragPreviewEl && this.dragPreviewEl.parentNode) {
            this.dragPreviewEl.parentNode.removeChild(this.dragPreviewEl)
        }
        this.dragPreviewEl = null
        this.isDragging = false
        const syncVisibleServicesByViewportAny = this.syncVisibleServicesByViewport as any
        if (syncVisibleServicesByViewportAny && typeof syncVisibleServicesByViewportAny.cancel === 'function') {
            syncVisibleServicesByViewportAny.cancel()
        }
        const applyDebouncedSearchAny = this.applyDebouncedSearch as any
        if (applyDebouncedSearchAny && typeof applyDebouncedSearchAny.cancel === 'function') {
            applyDebouncedSearchAny.cancel()
        }
    }
})
